import type { MouseEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Clock } from "lucide-react";
import { getArticle } from "../data/articles";
import type { Article } from "../data/articles";
import {
  articleDescription,
  articleOgDescription,
  articleTitle,
  articleUrl,
  buildArticleJsonLd,
  formatPublishedDate,
  LEAD_LABEL,
  lazyImages,
  splitContent,
} from "../lib/seo";
import { usePageMeta } from "../hooks/usePageMeta";
import { useJsonLd } from "../hooks/useJsonLd";
import { IndependenceBanner } from "../components/shared/IndependenceBanner";
import { Header } from "../components/shared/Header";
import { Footer } from "../components/shared/Footer";
import { ArticleBreadcrumb } from "../components/article/ArticleBreadcrumb";
import { ArticleCta } from "../components/article/ArticleCta";
import { ArticleFaq } from "../components/article/ArticleFaq";
import NotFoundPage from "../app/not-found";

// contentHtml vem apenas de src/data/articles.json (estático, validado em teste) — nunca de entrada do usuário.
function Html({ html, testId }: { html: string; testId: string }) {
  const navigate = useNavigate();
  if (!html) return null;
  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest("a");
    const href = a?.getAttribute("href");
    if (!a || !href?.startsWith("/") || a.target === "_blank" || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(href);
  };
  return <div data-testid={testId} className="article-content space-y-4" onClick={onClick} dangerouslySetInnerHTML={{ __html: lazyImages(html) }} />;
}

function ArticleBody({ article }: { article: Article }) {
  usePageMeta(articleTitle(article), articleDescription(article), articleUrl(article), { type: "article", description: articleOgDescription(article) });
  useJsonLd(buildArticleJsonLd(article));
  const [before, after] = splitContent(article.contentHtml);
  return (
    <div className="flex min-h-screen flex-col bg-[#0B0F17]">
      <IndependenceBanner />
      <Header />
      <main data-testid="article-page" className="flex-1">
        <article className="mx-auto max-w-3xl px-4 py-8 leading-relaxed text-slate-300 sm:px-6 sm:py-12">
          <ArticleBreadcrumb article={article} />
          <header className="mt-8">
            <p data-testid="article-meta" className="flex flex-wrap items-center gap-2 font-mono text-xs text-slate-500">
              <Clock aria-hidden="true" className="h-3.5 w-3.5 text-emerald-500" />
              <span>{article.readingTime} de leitura</span>
              <span aria-hidden="true">·</span>
              <time dateTime={article.publishedAt}>{formatPublishedDate(article.publishedAt)}</time>
            </p>
            <h1 data-testid="article-title" className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">{article.title}</h1>
            <p data-testid="article-subtitle" className="mt-4 text-base text-slate-400 md:text-lg">{article.subtitle}</p>
          </header>
          <section data-testid="article-lead-box" aria-labelledby="article-lead-title" className="mb-8 mt-8 rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-5">
            <h2 id="article-lead-title" className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-emerald-400">{LEAD_LABEL}</h2>
            <p className="mt-2 text-base leading-relaxed text-slate-100">{article.legalSummary}</p>
          </section>
          <Html html={before} testId="article-content" />
          <ArticleCta article={article} testId="article-cta-inline" />
          <Html html={after} testId="article-content-rest" />
          <ArticleFaq faqs={article.faqs} />
          <ArticleCta article={article} testId="article-cta-final" />
          <p data-testid="article-no-affiliation" className="mt-8 border-t border-slate-800 pt-6 text-xs leading-relaxed text-slate-500">
            O Gepeto.life não possui vínculo, parceria ou afiliação com {article.targetEntity.replace(/\.$/, "")}. A marca é citada apenas para identificação; este conteúdo é informativo e não constitui consultoria jurídica.
          </p>
        </article>
      </main>
      <Footer />
    </div>
  );
}

export default function ArticleView() {
  const { niche = "", slug = "" } = useParams();
  const article = getArticle(niche, slug);
  return article ? <ArticleBody key={`${niche}/${slug}`} article={article} /> : <NotFoundPage />;
}
